/**
 * SupportTicketsTab — admin UI to triage/resolve customer support tickets.
 * Consumes API: /api/admin/support/tickets + /reply + /status + DELETE
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  MessageSquare, Loader2, Filter, RefreshCw, Send, CheckCircle, ArrowRight, Trash2,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  open: { ar: 'مفتوح', en: 'Open', cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  in_progress: { ar: 'قيد المعالجة', en: 'In Progress', cls: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  resolved: { ar: 'تم الحل', en: 'Resolved', cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  closed: { ar: 'مغلق', en: 'Closed', cls: 'bg-[#404040]/40 text-[#a0a0a0] border-[#505050]/30' },
};

export default function SupportTicketsTab({ language, setActiveTab, headers }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [openCount, setOpenCount] = useState(0);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status_filter: statusFilter } : {};
      const res = await axios.get(`${BACKEND_URL}/api/admin/support/tickets`, { headers, params });
      setItems(res.data?.data || []);
      setOpenCount(res.data?.open_count || 0);
      setInProgressCount(res.data?.in_progress_count || 0);
    } catch (err) {
      console.error('Failed to load tickets:', err);
      toast.error(language === 'ar' ? 'فشل تحميل التذاكر' : 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, headers, language]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openTicket = async (id) => {
    setSelectedId(id);
    setLoadingDetail(true);
    setSelectedTicket(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/support/tickets/${id}`, { headers });
      setSelectedTicket(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
      setSelectedId(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedId) return;
    setSending(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/support/tickets/${selectedId}/reply`,
        { text: replyText.trim() },
        { headers }
      );
      setReplyText('');
      toast.success(language === 'ar' ? 'تم إرسال الرد' : 'Reply sent');
      await openTicket(selectedId);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (newStatus) => {
    if (!selectedId) return;
    setProcessingId(selectedId);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/support/tickets/${selectedId}/status`,
        { status: newStatus },
        { headers }
      );
      toast.success(language === 'ar' ? 'تم التحديث' : 'Updated');
      await openTicket(selectedId);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setProcessingId(null);
    }
  };

  const deleteTicket = async (id) => {
    if (!window.confirm(language === 'ar' ? 'تأكيد حذف التذكرة؟' : 'Confirm delete?')) return;
    setProcessingId(id);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/support/tickets/${id}`, { headers });
      toast.success(language === 'ar' ? 'تم الحذف' : 'Deleted');
      if (selectedId === id) {
        setSelectedId(null);
        setSelectedTicket(null);
      }
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setProcessingId(null);
    }
  };

  // ===== Detail View =====
  if (selectedId) {
    const status = selectedTicket ? (STATUS_LABEL[selectedTicket.status] || STATUS_LABEL.open) : null;
    return (
      <div data-testid="support-ticket-detail">
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => { setSelectedId(null); setSelectedTicket(null); }}
            variant="outline"
            className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
            data-testid="ticket-detail-back-btn"
          >
            <ArrowRight className="h-4 w-4 me-2" />
            {language === 'ar' ? 'رجوع' : 'Back'}
          </Button>
          {selectedTicket && status && (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs border ${status.cls}`}>
              {language === 'ar' ? status.ar : status.en}
            </span>
          )}
        </div>

        {loadingDetail || !selectedTicket ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-400 mx-auto" />
          </div>
        ) : (
          <>
            {/* Ticket Header */}
            <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] p-6 mb-4">
              <h3 className="text-lg font-bold text-white mb-2">{selectedTicket.subject || '—'}</h3>
              <div className="flex items-center gap-4 text-sm text-[#808080]">
                <span>{selectedTicket.user?.name || '—'}</span>
                <span>·</span>
                <span>{selectedTicket.user?.email || '—'}</span>
                {selectedTicket.priority && (
                  <>
                    <span>·</span>
                    <span className="text-amber-400">{selectedTicket.priority}</span>
                  </>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] p-6 mb-4 space-y-4 max-h-[400px] overflow-y-auto">
              {(selectedTicket.messages || []).length === 0 ? (
                <p className="text-center text-[#808080]">{language === 'ar' ? 'لا توجد رسائل' : 'No messages'}</p>
              ) : (
                selectedTicket.messages.map((m) => (
                  <div
                    key={m.id || `${m.author}-${m.created_at}`}
                    className={`flex ${m.author === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                        m.author === 'admin'
                          ? 'bg-blue-600/20 border border-blue-500/30 text-white'
                          : 'bg-[#0a0a0a] border border-[#2a2a4e] text-[#c9c9c9]'
                      }`}
                    >
                      <p className="text-xs text-[#808080] mb-1">
                        {m.author === 'admin' ? (language === 'ar' ? 'الأدمن' : 'Admin') : (language === 'ar' ? 'العميل' : 'Customer')}
                        {' · '}
                        {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                      </p>
                      <p className="whitespace-pre-wrap">{m.text}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Reply + Actions */}
            <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] p-4">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={language === 'ar' ? 'اكتب ردك...' : 'Type your reply...'}
                className="w-full h-24 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl p-3 text-white placeholder:text-[#606060] resize-none focus:border-blue-500 focus:outline-none"
                data-testid="ticket-reply-input"
              />
              <div className="flex items-center justify-between mt-3 gap-2">
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => changeStatus('resolved')}
                    disabled={processingId === selectedId || selectedTicket.status === 'resolved'}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="ticket-resolve-btn"
                  >
                    <CheckCircle className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'تم الحل' : 'Resolve'}
                  </Button>
                  <Button
                    onClick={() => changeStatus('closed')}
                    disabled={processingId === selectedId || selectedTicket.status === 'closed'}
                    variant="outline"
                    className="border-[#2a2a4e] text-white"
                    data-testid="ticket-close-btn"
                  >
                    {language === 'ar' ? 'إغلاق' : 'Close'}
                  </Button>
                </div>
                <Button
                  onClick={sendReply}
                  disabled={sending || !replyText.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="ticket-send-reply-btn"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 me-2" />}
                  {language === 'ar' ? 'إرسال' : 'Send'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ===== List View =====
  return (
    <div data-testid="support-tickets-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <MessageSquare className="h-6 w-6 text-blue-400" />
          {language === 'ar' ? 'تذاكر الدعم' : 'Support Tickets'}
          {openCount > 0 && (
            <span className="ms-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30">
              {openCount} {language === 'ar' ? 'مفتوح' : 'open'}
            </span>
          )}
          {inProgressCount > 0 && (
            <span className="ms-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
              {inProgressCount} {language === 'ar' ? 'قيد المعالجة' : 'in progress'}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setActiveTab('main')}
            variant="outline"
            className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] rounded-xl"
            data-testid="support-tickets-back-btn"
          >
            رجوع
          </Button>
          <Button
            onClick={fetchItems}
            variant="outline"
            className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] rounded-xl"
            data-testid="support-tickets-refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-4">
        <Filter className="h-4 w-4 text-[#808080]" />
        {['all', 'open', 'in_progress', 'resolved', 'closed'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              statusFilter === s
                ? 'bg-blue-500/30 text-blue-300 border border-blue-500/40'
                : 'bg-[#1a1a2e] text-[#808080] border border-[#2a2a4e] hover:text-white'
            }`}
            data-testid={`support-filter-${s}`}
          >
            {s === 'all' ? (language === 'ar' ? 'الكل' : 'All') : STATUS_LABEL[s][language === 'ar' ? 'ar' : 'en']}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-400 mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-[#808080]">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>{language === 'ar' ? 'لا توجد تذاكر' : 'No tickets'}</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#2a2a4e]/50">
            {items.map((t) => {
              const status = STATUS_LABEL[t.status] || STATUS_LABEL.open;
              return (
                <li
                  key={t.id}
                  className="px-4 py-3 hover:bg-[#0a0a0a]/40 flex items-center justify-between gap-3"
                >
                  <button
                    onClick={() => openTicket(t.id)}
                    className="flex-1 text-start"
                    data-testid={`open-ticket-${t.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-white font-medium">{t.subject || '—'}</h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${status.cls}`}>
                        {language === 'ar' ? status.ar : status.en}
                      </span>
                    </div>
                    <div className="text-xs text-[#808080] flex items-center gap-2">
                      <span>{t.user?.name || '—'}</span>
                      <span>·</span>
                      <span>{t.user?.email || ''}</span>
                      <span>·</span>
                      <span>
                        {(t.messages_count || 0)} {language === 'ar' ? 'رسالة' : 'msgs'}
                      </span>
                      {t.priority && (
                        <>
                          <span>·</span>
                          <span className="text-amber-400">{t.priority}</span>
                        </>
                      )}
                    </div>
                  </button>
                  <Button
                    size="sm"
                    onClick={() => deleteTicket(t.id)}
                    disabled={processingId === t.id}
                    variant="outline"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-7 px-2"
                    data-testid={`delete-ticket-${t.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
